import React from 'react';

import { Icon } from '@column/column-ui-kit';
import { Button } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { EntitySelector, SelectedCard } from '~/lib/components/selectors';
import { useNavigate } from '~/lib/navigation';
import { type EntityWithDetails } from '~/repositories/EntityRepository';
import { useSessionStore } from '~/stores/Session';

const noop = () => undefined;

interface Props {
  entity?: EntityWithDetails | null;
  isLoading: boolean;
  isError: boolean;
  isNotFound: boolean;
}

// The platform's root entity, shown like a locked entity selector, with an arrow that opens the root entity page.
export const RootEntityField: React.FC<Props> = ({ entity, isLoading, isError, isNotFound }) => {
  const navigate = useNavigate();
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const openRootEntity = () => navigate(ROUTE.ENTITIES_ROOT_ENTITY);

  // Laid out like the user profile's Authenticator App field: a short explanation with the action beside it.
  if (isNotFound) {
    return (
      <div
        data-cy="root-entity-empty"
        className="flex items-center gap-4 rounded-lg bg-gray-25 px-4 py-3 ring-1 ring-black/5 dark:bg-white/[0.02] dark:ring-white/10"
      >
        <div className="flex flex-auto items-center gap-2 text-sm leading-5 text-muted-foreground">
          <Icon.Document className="shrink-0 text-gray-400 [--icon-size-x:16px] [--icon-size-y:16px]" />
          <span>
            {isSandbox
              ? 'Select the entity that owns this platform.'
              : 'Your live root entity is created when you open a Commercial Account.'}
          </span>
        </div>
        <Button type="button" size="sm" onClick={openRootEntity}>
          <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />
          Create Root Entity
        </Button>
      </div>
    );
  }

  if (entity?.id) {
    return (
      <EntitySelector
        value={entity.id}
        onChange={noop}
        onBlur={noop}
        locked
        onOpen={openRootEntity}
        openLabel="Open root entity"
      />
    );
  }

  return (
    <SelectedCard
      open
      title="Root Entity"
      rows={[]}
      isLoading={isLoading}
      alert={isError && !isLoading ? 'Failed to load root entity' : undefined}
    />
  );
};
